import { useEffect, useRef, useState } from "react";
import {
  AlertCircle,
  CheckCircle2,
  ImagePlus,
  Info,
  Loader2,
  Trash2,
  UploadCloud,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import {
  ModerationRulesetService,
  type ModerationImageScope,
  type ModerationReferenceRole,
  type ModerationRule,
  type ModerationRuleWrite,
  type ModerationRunAt,
  type ModerationSeverity,
} from "@/services/moderation-ruleset.service";

interface RuleEditorDialogProps {
  rulesetId: string;
  rule: ModerationRule | null;
  onClose: () => void;
  onSaved: (rule: ModerationRule) => void | Promise<void>;
}

type ReferenceDraft =
  | {
      kind: "existing";
      key: string;
      assetId: string;
      assetUrl: string;
      role: ModerationReferenceRole;
      caption: string | null;
    }
  | {
      kind: "pending";
      key: string;
      file: File;
      assetUrl: string;
      role: ModerationReferenceRole;
      caption: string | null;
    };

const MAX_REFERENCE_BYTES = 10 * 1024 * 1024;
const REFERENCE_IMAGE_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
  "image/avif",
]);
const REFERENCE_IMAGE_ACCEPT = [...REFERENCE_IMAGE_TYPES].join(",");

const timingOptions: Array<{
  value: ModerationRunAt;
  title: string;
  description: string;
}> = [
  {
    value: "before_enrichment",
    title: "Before enrichment",
    description: "Check the original product data as it was submitted.",
  },
  {
    value: "after_enrichment",
    title: "After enrichment",
    description: "Check the enriched product data after processing is complete.",
  },
  {
    value: "both",
    title: "Before and after",
    description: "Run two separate checks: once on each version of the product data.",
  },
];

const imageOptions: Array<{
  value: ModerationImageScope;
  title: string;
  description: string;
}> = [
  {
    value: "first_image",
    title: "First product image",
    description: "Only inspect the product's primary image.",
  },
  {
    value: "all_images",
    title: "All images (up to 6)",
    description: "Inspect the first six product images in their catalogue order.",
  },
];

const SLUG_PATTERN = /^[a-z0-9]+(?:[-_][a-z0-9]+)*$/;

function normalizeSlugInput(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9_-]+/g, "-").replace(/^-+/, "");
}

function slugFromName(value: string): string {
  return value
    .normalize("NFKC")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 96);
}


function timingHelp(): React.ReactNode {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label="About moderation timing"
            className="rounded-sm text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Info className="h-4 w-4" />
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs text-xs leading-5" side="right">
          Before uses the product data exactly as submitted. After uses data added or improved by
          enrichment. Both produces an independent result for each stage.
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function RuleEditorDialog({
  rulesetId,
  rule,
  onClose,
  onSaved,
}: RuleEditorDialogProps) {
  const [name, setName] = useState(rule?.name ?? "");
  const [slug, setSlug] = useState(rule?.slug ?? "");
  const [slugTouched, setSlugTouched] = useState(Boolean(rule));
  const [description, setDescription] = useState(rule?.description ?? "");
  const [severity, setSeverity] = useState<ModerationSeverity>(rule?.severity ?? "medium");
  const [runAt, setRunAt] = useState<ModerationRunAt>(rule?.runAt ?? "after_enrichment");
  const [imageScope, setImageScope] = useState<ModerationImageScope>(
    rule?.imageScope ?? "first_image"
  );
  const [autoUnpublish, setAutoUnpublish] = useState(rule?.autoUnpublish ?? false);
  const [references, setReferences] = useState<ReferenceDraft[]>(
    (rule?.referenceImages ?? []).map((reference) => ({
      kind: "existing",
      key: reference.assetId,
      assetId: reference.assetId,
      assetUrl: reference.assetUrl,
      role: reference.role,
      caption: reference.caption,
    }))
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const objectUrls = useRef(new Set<string>());

  useEffect(() => {
    const urls = objectUrls.current;
    return () => urls.forEach((url) => URL.revokeObjectURL(url));
  }, []);

  function changeName(value: string) {
    setName(value);
    // Track the name until the user overrides it; an existing rule never re-slugs.
    if (!rule && !slugTouched) setSlug(slugFromName(value));
  }

  function addReferenceFiles(role: ModerationReferenceRole, files: File[]) {
    setError(null);
    const accepted: ReferenceDraft[] = [];
    for (const file of files) {
      if (!REFERENCE_IMAGE_TYPES.has(file.type)) {
        setError("Reference examples must be JPEG, PNG, WebP, GIF, or AVIF images.");
        continue;
      }
      if (file.size > MAX_REFERENCE_BYTES) {
        setError(`${file.name} is larger than the 10 MB reference-image limit.`);
        continue;
      }
      const assetUrl = URL.createObjectURL(file);
      objectUrls.current.add(assetUrl);
      accepted.push({
        kind: "pending",
        key: `${file.name}-${file.size}-${file.lastModified}-${crypto.randomUUID()}`,
        file,
        assetUrl,
        role,
        // Newly uploaded references have no caption yet; the API accepts null.
        caption: null,
      });
    }
    setReferences((current) => [...current, ...accepted].slice(0, 100));
  }

  function removeReference(reference: ReferenceDraft) {
    if (reference.kind === "pending") {
      URL.revokeObjectURL(reference.assetUrl);
      objectUrls.current.delete(reference.assetUrl);
    }
    setReferences((current) => current.filter((item) => item.key !== reference.key));
  }

  function validate(): string | null {
    if (!name.trim()) return "Enter a rule name.";
    if (!description.trim()) return "Describe what the rule should flag and what should be accepted.";
    return null;
  }

  async function save() {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const referenceImages = await Promise.all(
        references.map(async (reference) => {
          if (reference.kind === "existing") {
            // The caption must go back with the asset: the API replaces the
            // reference list wholesale, so dropping it here erases the
            // customer's reject reason on an unrelated edit.
            return {
              assetId: reference.assetId,
              role: reference.role,
              caption: reference.caption,
            };
          }
          const uploaded = await ModerationRulesetService.uploadReferenceAsset(reference.file);
          return {
            assetId: uploaded.assetId,
            role: reference.role,
            caption: reference.caption,
          };
        })
      );
      const payload: ModerationRuleWrite = {
        name: name.trim(),
        // Only on create: the slug is fixed once the rule exists.
        ...(rule ? {} : { slug }),
        description: description.trim(),
        severity,
        runAt,
        imageScope,
        referenceImages,
        autoUnpublish,
      };
      const saved = rule
        ? await ModerationRulesetService.updateRule(rulesetId, rule.id, payload, rule.version)
        : await ModerationRulesetService.createRule(rulesetId, payload);
      await onSaved(saved);
      onClose();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The rule could not be saved.");
    } finally {
      setSaving(false);
    }
  }

  const isValid = Boolean(name.trim() && description.trim() && SLUG_PATTERN.test(slug));

  return (
    <Dialog open onOpenChange={(open) => !open && !saving && onClose()}>
      <DialogContent className="grid max-h-[calc(100vh-2rem)] grid-rows-[auto_minmax(0,1fr)_auto] gap-0 overflow-hidden p-0 sm:max-w-4xl">
        <DialogHeader className="border-b px-6 py-5 pr-12">
          <DialogTitle>{rule ? "Edit moderation rule" : "Create moderation rule"}</DialogTitle>
          <DialogDescription>
            {rule
              ? `Saving a policy change creates the next version. Version ${rule.effectiveVersion ?? rule.version} keeps running until the update is ready.`
              : "Describe the policy in your own words. The rule starts running automatically when it is ready."}
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 overflow-y-auto px-6 py-5">
          {error ? (
            <div
              role="alert"
              className="mb-5 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300"
            >
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          ) : null}

          <div className="space-y-8">
            <section aria-labelledby="rule-basics-heading" className="space-y-4">
              <div>
                <h3 id="rule-basics-heading" className="text-base font-semibold">Basics</h3>
                <p className="text-xs text-muted-foreground">
                  The slug is returned with violations so your integration can identify the rule.
                </p>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="moderation-rule-name">Rule name</Label>
                  <Input
                    id="moderation-rule-name"
                    value={name}
                    onChange={(event) => changeName(event.target.value)}
                    placeholder="Prohibited weapons"
                    autoFocus
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="moderation-rule-slug">Rule slug</Label>
                  <Input
                    id="moderation-rule-slug"
                    className="font-mono"
                    value={slug}
                    onChange={(event) => {
                      setSlugTouched(true);
                      setSlug(normalizeSlugInput(event.target.value));
                    }}
                    readOnly={Boolean(rule)}
                    disabled={Boolean(rule)}
                    placeholder="prohibited-weapons"
                    aria-describedby="moderation-rule-slug-help"
                  />
                  <p id="moderation-rule-slug-help" className="text-xs text-muted-foreground">
                    {rule
                      ? "Fixed when the rule was created — existing violations reference it, so it cannot change."
                      : "Defaults to the rule name. Lowercase letters and numbers, separated by dashes or underscores."}
                  </p>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="moderation-rule-description">Detailed description</Label>
                <Textarea
                  id="moderation-rule-description"
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                  className="min-h-36 resize-y"
                  placeholder="Explain what should be flagged, important edge cases, and legitimate examples that should remain acceptable."
                />
              </div>
            </section>

            <section aria-labelledby="rule-timing-heading" className="space-y-3">
              <div className="flex items-center gap-2">
                <h3 id="rule-timing-heading" className="text-base font-semibold">When should it run?</h3>
                {timingHelp()}
              </div>
              <div className="grid gap-3 md:grid-cols-3">
                {timingOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    aria-pressed={runAt === option.value}
                    onClick={() => setRunAt(option.value)}
                    className={cn(
                      "rounded-md border p-4 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                      runAt === option.value
                        ? "border-foreground bg-muted/50"
                        : "border-border hover:bg-muted/30"
                    )}
                  >
                    <span className="flex items-center justify-between gap-3 text-sm font-medium">
                      {option.title}
                      {runAt === option.value ? <CheckCircle2 className="h-4 w-4" /> : null}
                    </span>
                    <span className="mt-1 block text-xs leading-5 text-muted-foreground">
                      {option.description}
                    </span>
                  </button>
                ))}
              </div>
            </section>

            <section aria-labelledby="rule-images-heading" className="space-y-3">
              <div>
                <h3 id="rule-images-heading" className="text-base font-semibold">Product images</h3>
                <p className="text-xs text-muted-foreground">
                  “All images” is capped at the first 6 images for each product.
                </p>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                {imageOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    aria-pressed={imageScope === option.value}
                    onClick={() => setImageScope(option.value)}
                    className={cn(
                      "rounded-md border p-4 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                      imageScope === option.value
                        ? "border-foreground bg-muted/50"
                        : "border-border hover:bg-muted/30"
                    )}
                  >
                    <span className="flex items-center justify-between gap-3 text-sm font-medium">
                      {option.title}
                      {imageScope === option.value ? <CheckCircle2 className="h-4 w-4" /> : null}
                    </span>
                    <span className="mt-1 block text-xs leading-5 text-muted-foreground">
                      {option.description}
                    </span>
                  </button>
                ))}
              </div>
            </section>

            <section aria-labelledby="rule-examples-heading" className="space-y-3">
              <div>
                <h3 id="rule-examples-heading" className="text-base font-semibold">Reference examples</h3>
                <p className="text-xs text-muted-foreground">
                  Optional examples help clarify borderline cases. Images can be up to 10 MB each.
                </p>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <ReferenceDropZone
                  role="flagged"
                  title="Flagged examples"
                  description="Products that should violate this rule."
                  references={references.filter((reference) => reference.role === "flagged")}
                  onAdd={addReferenceFiles}
                  onRemove={removeReference}
                />
                <ReferenceDropZone
                  role="acceptable"
                  title="Acceptable examples"
                  description="Similar products that should be allowed."
                  references={references.filter((reference) => reference.role === "acceptable")}
                  onAdd={addReferenceFiles}
                  onRemove={removeReference}
                />
              </div>
            </section>

            <section aria-labelledby="rule-enforcement-heading" className="space-y-4">
              <h3 id="rule-enforcement-heading" className="text-base font-semibold">Enforcement</h3>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="moderation-rule-severity">Severity</Label>
                  <select
                    id="moderation-rule-severity"
                    value={severity}
                    onChange={(event) => setSeverity(event.target.value as ModerationSeverity)}
                    className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
                <div className="flex items-start justify-between gap-4 rounded-md border p-4">
                  <div>
                    <Label htmlFor="moderation-rule-unpublish">Auto-unpublish product</Label>
                    <p className="mt-1 text-xs leading-5 text-muted-foreground">
                      Immediately unpublish a product when this rule is violated.
                    </p>
                  </div>
                  <Switch
                    id="moderation-rule-unpublish"
                    checked={autoUnpublish}
                    onCheckedChange={setAutoUnpublish}
                  />
                </div>
              </div>
            </section>
          </div>
        </div>

        <DialogFooter className="shrink-0 gap-2 border-t bg-background px-6 py-4">
          <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="button" onClick={() => void save()} disabled={!isValid || saving}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {saving ? "Saving…" : rule ? "Save new version" : "Create rule"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReferenceDropZone({
  role,
  title,
  description,
  references,
  onAdd,
  onRemove,
}: {
  role: ModerationReferenceRole;
  title: string;
  description: string;
  references: ReferenceDraft[];
  onAdd: (role: ModerationReferenceRole, files: File[]) => void;
  onRemove: (reference: ReferenceDraft) => void;
}) {
  return (
    <div className="rounded-md border p-4">
      <div className="flex items-start gap-3">
        <div className="rounded-md bg-muted p-2">
          <ImagePlus className="h-4 w-4" />
        </div>
        <div>
          <p className="text-sm font-medium">{title}</p>
          <p className="text-xs text-muted-foreground">{description}</p>
        </div>
      </div>

      {references.length > 0 ? (
        <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-3">
          {references.map((reference) => (
            <div key={reference.key} className="group relative aspect-square overflow-hidden rounded-md border bg-muted">
              <img src={reference.assetUrl} alt={`${title} reference`} className="h-full w-full object-cover" />
              <button
                type="button"
                onClick={() => onRemove(reference)}
                aria-label={`Remove ${title.toLowerCase()} reference`}
                className="absolute right-1 top-1 rounded-sm bg-background/90 p-1 text-muted-foreground opacity-0 shadow-sm transition-opacity hover:text-destructive focus:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring group-hover:opacity-100"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      ) : null}

      <label
        className="mt-3 flex cursor-pointer flex-col items-center justify-center rounded-md border border-dashed p-4 text-center transition-colors hover:bg-muted/30 focus-within:ring-2 focus-within:ring-ring"
        onDragOver={(event) => event.preventDefault()}
        onDrop={(event) => {
          event.preventDefault();
          onAdd(role, Array.from(event.dataTransfer.files));
        }}
      >
        <UploadCloud className="mb-1 h-5 w-5 text-muted-foreground" />
        <span className="text-xs font-medium">Drop images here or browse</span>
        <input
          type="file"
          accept={REFERENCE_IMAGE_ACCEPT}
          multiple
          className="sr-only"
          onChange={(event) => {
            onAdd(role, Array.from(event.target.files ?? []));
            event.target.value = "";
          }}
        />
      </label>
    </div>
  );
}
